import type { Quote, QuoteInsert, QuoteUpdate, QuoteItem, QuoteItemInsert } from '@/types/quotes';
import { mockComponentPricingService } from './mock-component-pricing';
import { normalizeAgreementTerms } from '@/types/agreement-terms';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Mock quotes data
const mockQuotes: Quote[] = [];
const mockQuoteItems: QuoteItem[] = [];

type QuoteItemDraft = Omit<QuoteItemInsert, 'quote_id'>;

function updateMockQuoteStatusRecord(quoteId: string, status: Quote['status']): Quote {
  const index = mockQuotes.findIndex((quote) => quote.id === quoteId);
  if (index === -1) {
    throw new Error('Quote not found');
  }

  const existing = mockQuotes[index];
  if (!existing) {
    throw new Error('Quote not found');
  }

  const updated: Quote = {
    ...existing,
    status,
    updated_at: new Date().toISOString(),
  };
  mockQuotes[index] = updated;
  return updated;
}

export const mockQuotesService = {
  async getAll(): Promise<Quote[]> {
    await delay(300);
    return [...mockQuotes];
  },

  async getById(id: string): Promise<Quote | null> {
    await delay(200);
    return mockQuotes.find((quote) => quote.id === id) ?? null;
  },

  async getByLocationId(locationId: string): Promise<Quote[]> {
    await delay(200);
    return mockQuotes.filter((quote) => quote.location_id === locationId);
  },

  async getItemsByQuoteId(quoteId: string): Promise<QuoteItem[]> {
    await delay(200);
    return mockQuoteItems.filter((item) => item.quote_id === quoteId).sort((a, b) => a.line_number - b.line_number);
  },

  async create(data: QuoteInsert, items: Omit<QuoteItemInsert, 'quote_id'>[] = []): Promise<Quote> {
    await delay(400);

    // Calculate prices for items with component_id
    const itemsWithPrices: QuoteItemDraft[] = await Promise.all(
      items.map(async (item): Promise<QuoteItemDraft> => {
        let unitPrice = item.unit_price;
        let lineTotal = item.line_total;

        // If item has component_id, fetch price and calculate totals
        if (item.component_id) {
          try {
            unitPrice = await mockComponentPricingService.getPriceForComponent(item.component_id);
            lineTotal = item.quantity * unitPrice;
          } catch (error) {
            console.error(`Error getting price for component ${item.component_id}:`, error);
            // Use provided values if price lookup fails
            unitPrice = item.unit_price;
            lineTotal = item.line_total;
          }
        }

        return {
          line_number: item.line_number,
          component_id: item.component_id ?? null,
          display_type_id: item.display_type_id ?? null,
          description: item.description,
          notes: item.notes ?? null,
          quantity: item.quantity,
          unit_price: unitPrice,
          line_total: lineTotal,
        };
      })
    );

    // Calculate subtotal, tax, and total
    const subtotal = itemsWithPrices.reduce((sum, item) => sum + item.line_total, 0);
    const tax = 0; // Placeholder for now
    const total = subtotal + tax;

    const newQuote: Quote = {
      id: `quote-${Date.now()}`,
      location_id: data.location_id ?? null,
      contact_id: data.contact_id ?? null,
      customer_name: data.customer_name ?? null,
      deal_id: data.deal_id ?? null,
      quote_number: data.quote_number,
      status: data.status ?? 'draft',
      valid_until: data.valid_until ?? null,
      subtotal: data.subtotal ?? subtotal,
      tax: data.tax ?? tax,
      total: data.total ?? total,
      notes: data.notes ?? null,
      agreement_terms: normalizeAgreementTerms(data.agreement_terms),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: null,
    };
    mockQuotes.push(newQuote);

    // Create items
    if (itemsWithPrices.length > 0) {
      itemsWithPrices.forEach((item, index) => {
        const newItem: QuoteItem = {
          id: `quote-item-${Date.now()}-${index}`,
          quote_id: newQuote.id,
          line_number: item.line_number,
          component_id: item.component_id ?? null,
          display_type_id: item.display_type_id ?? null,
          description: item.description,
          notes: item.notes ?? null,
          quantity: item.quantity,
          unit_price: item.unit_price,
          line_total: item.line_total,
          created_at: new Date().toISOString(),
        };
        mockQuoteItems.push(newItem);
      });
    }

    return newQuote;
  },

  async update(id: string, data: QuoteUpdate): Promise<Quote> {
    await delay(400);
    const index = mockQuotes.findIndex((quote) => quote.id === id);
    if (index === -1) {
      throw new Error('Quote not found');
    }
    const existing = mockQuotes[index];
    if (!existing) {
      throw new Error('Quote not found');
    }
    const updated: Quote = {
      ...existing,
      ...data,
      agreement_terms:
        data.agreement_terms !== undefined ? normalizeAgreementTerms(data.agreement_terms) : existing.agreement_terms,
      updated_at: new Date().toISOString(),
    };
    mockQuotes[index] = updated;
    if (existing.location_id !== updated.location_id) {
      const { refreshAllMockPaidWorkAssociations } = await import('./mock-invoice-paid-work');
      await refreshAllMockPaidWorkAssociations();
    }
    return updated;
  },

  async replaceItems(quoteId: string, items: Omit<QuoteItemInsert, 'quote_id'>[]): Promise<void> {
    await delay(400);

    // Calculate prices for items with component_id
    const itemsWithPrices: QuoteItemDraft[] = await Promise.all(
      items.map(async (item): Promise<QuoteItemDraft> => {
        let unitPrice = item.unit_price;
        let lineTotal = item.line_total;

        // If item has component_id, fetch price and calculate totals
        if (item.component_id) {
          try {
            unitPrice = await mockComponentPricingService.getPriceForComponent(item.component_id);
            lineTotal = item.quantity * unitPrice;
          } catch (error) {
            console.error(`Error getting price for component ${item.component_id}:`, error);
            // Use provided values if price lookup fails
            unitPrice = item.unit_price;
            lineTotal = item.line_total;
          }
        }

        return {
          line_number: item.line_number,
          component_id: item.component_id ?? null,
          display_type_id: item.display_type_id ?? null,
          description: item.description,
          notes: item.notes ?? null,
          quantity: item.quantity,
          unit_price: unitPrice,
          line_total: lineTotal,
        };
      })
    );

    // Calculate subtotal, tax, and total
    const subtotal = itemsWithPrices.reduce((sum, item) => sum + item.line_total, 0);
    const tax = 0; // Placeholder for now
    const total = subtotal + tax;

    // Update quote totals
    const quoteIndex = mockQuotes.findIndex((q) => q.id === quoteId);
    if (quoteIndex !== -1) {
      const existingQuote = mockQuotes[quoteIndex];
      if (!existingQuote) {
        throw new Error('Quote not found');
      }
      mockQuotes[quoteIndex] = {
        ...existingQuote,
        subtotal,
        tax,
        total,
        updated_at: new Date().toISOString(),
      };
    }

    // Delete existing items
    const indices = mockQuoteItems
      .map((item, idx) => (item.quote_id === quoteId ? idx : -1))
      .filter((idx) => idx !== -1)
      .reverse();
    indices.forEach((idx) => mockQuoteItems.splice(idx, 1));

    // Add new items
    itemsWithPrices.forEach((item, index) => {
      const newItem: QuoteItem = {
        id: `quote-item-${Date.now()}-${index}`,
        quote_id: quoteId,
        line_number: item.line_number,
        component_id: item.component_id ?? null,
        display_type_id: item.display_type_id ?? null,
        description: item.description,
        notes: item.notes ?? null,
        quantity: item.quantity,
        unit_price: item.unit_price,
        line_total: item.line_total,
        created_at: new Date().toISOString(),
      };
      mockQuoteItems.push(newItem);
    });
  },

  async setStatus(quoteId: string, status: Quote['status']): Promise<Quote> {
    await delay(300);
    if (status === 'converted') {
      throw new Error('Use quote conversion to create an invoice and mark a quote as converted');
    }
    return updateMockQuoteStatusRecord(quoteId, status);
  },

  async convertToDeal(quoteId: string): Promise<string> {
    await delay(500);
    const quote = await this.getById(quoteId);
    if (!quote) {
      throw new Error('Quote not found');
    }

    // Dynamically import mockDealsService to avoid circular dependency
    const { mockDealsService } = await import('./mock-deals');

    // Create deal from quote
    const deal = await mockDealsService.create({
      location_id: quote.location_id,
      contact_id: quote.contact_id,
      name: `Deal from Quote ${quote.quote_number}`,
      stage: 'qualified',
      total_contract_value: quote.total,
      upfront_amount: quote.total,
      notes: `Converted from quote ${quote.quote_number}. ${quote.notes || ''}`,
    });

    // Update quote status to accepted
    await this.setStatus(quoteId, 'accepted');

    return deal.id;
  },

  async convertToInvoice(quoteId: string): Promise<string> {
    await delay(500);
    const quote = await this.getById(quoteId);
    if (!quote) {
      throw new Error('Quote not found');
    }

    const items = await this.getItemsByQuoteId(quoteId);
    const { mockInvoicesService } = await import('./mock-invoices');

    const year = new Date().getFullYear();
    const existingInvoices = await mockInvoicesService.getAll();
    const invoiceNumbers = existingInvoices
      .map((inv) => inv.invoice_number)
      .filter((num) => num.startsWith(`INV-${year}-`))
      .map((num) => {
        const match = num.match(/INV-\d{4}-(\d+)/);
        const suffix = match?.[1];
        return suffix ? parseInt(suffix, 10) : 0;
      });
    const nextNumber = invoiceNumbers.length > 0 ? Math.max(...invoiceNumbers) + 1 : 1;
    const invoiceNumber = `INV-${year}-${String(nextNumber).padStart(3, '0')}`;

    let resolvedLocationId = quote.location_id;
    if (!resolvedLocationId && quote.deal_id) {
      const { mockDealsService } = await import('./mock-deals');
      const linkedDeal = await mockDealsService.getById(quote.deal_id);
      resolvedLocationId = linkedDeal?.location_id || null;
    }

    let resolvedCustomerName = quote.customer_name?.trim() || null;
    if (!resolvedCustomerName && resolvedLocationId) {
      const { mockLocationsService } = await import('./mock-locations');
      const linkedLocation = await mockLocationsService.getById(resolvedLocationId);
      resolvedCustomerName = linkedLocation?.location_name || null;
    }
    if (!resolvedCustomerName) {
      resolvedCustomerName = `Invoice for ${quote.quote_number}`;
    }
    const lineItems = items.map((item) => ({
      line_number: item.line_number,
      component_id: item.component_id || null,
      display_type_id: item.display_type_id || null,
      description: item.description,
      notes: item.notes || null,
      quantity: item.quantity,
      unit_price: item.unit_price,
      line_total: item.line_total,
    }));

    const invoice = await mockInvoicesService.create({
      location_id: resolvedLocationId,
      deal_id: quote.deal_id || null,
      quote_id: quote.id,
      location_order_id: null,
      invoice_number: invoiceNumber,
      customer_name: resolvedCustomerName,
      status: 'draft',
      due_date: null,
      line_items: lineItems,
      subtotal: quote.subtotal,
      tax: quote.tax,
      total: quote.total,
      notes: `Converted from quote ${quote.quote_number}. ${quote.notes || ''}`,
      agreement_terms: quote.agreement_terms,
    });

    updateMockQuoteStatusRecord(quoteId, 'converted');
    return invoice.id;
  },

  async duplicate(sourceQuoteId: string): Promise<Quote> {
    await delay(300);
    const source = await this.getById(sourceQuoteId);
    if (!source) {
      throw new Error('Quote not found');
    }

    const items = await this.getItemsByQuoteId(sourceQuoteId);
    const { customerNameForUnassignedDuplicate, suggestDuplicateDocumentNumber } = await import(
      '@/lib/utils/sales-document-numbers'
    );
    const quoteNumber = suggestDuplicateDocumentNumber(
      source.quote_number,
      mockQuotes.map((quote) => quote.quote_number)
    );

    return this.create(
      {
        location_id: null,
        contact_id: null,
        customer_name: customerNameForUnassignedDuplicate(null),
        deal_id: null,
        quote_number: quoteNumber,
        status: 'draft',
        valid_until: source.valid_until,
        subtotal: source.subtotal,
        tax: source.tax,
        total: source.total,
        notes: source.notes ? `${source.notes}\n\nDuplicated from ${source.quote_number}.` : `Duplicated from ${source.quote_number}.`,
        agreement_terms: source.agreement_terms,
      },
      items.map((item) => ({
        line_number: item.line_number,
        component_id: item.component_id ?? null,
        display_type_id: item.display_type_id ?? null,
        description: item.description,
        notes: item.notes ?? null,
        quantity: item.quantity,
        unit_price: item.unit_price,
        line_total: item.line_total,
      }))
    );
  },

  async delete(id: string): Promise<void> {
    await delay(300);
    // Delete items
    const indices = mockQuoteItems
      .map((item, idx) => (item.quote_id === id ? idx : -1))
      .filter((idx) => idx !== -1)
      .reverse();
    indices.forEach((idx) => mockQuoteItems.splice(idx, 1));

    // Delete quote
    const index = mockQuotes.findIndex((quote) => quote.id === id);
    if (index !== -1) {
      mockQuotes.splice(index, 1);
    }
  },
};
